import { Box, Button, Chip, Divider, Group, Modal, ScrollArea, Stack, Text, Textarea } from "@mantine/core"
import { ClockCounterClockwiseIcon } from "@phosphor-icons/react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import { DeliveryApi } from "../../apis"
import type { IDeliveryAuditLogEntry } from "../../apis/delivery"
import { IDeliveryNotePlus } from "../../models/DeliveryNote"
import { dateTimeSecFormat } from "../../utils/date"
import { message } from "../../utils/message"
import { DeliveryActionButton } from "../delivery/action-controls"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { translateBackendError } from "../delivery/translate-error"
import classes from "./pending.module.css"

const MAX_LENGTH = 2000

const PRESETS = [
  "Бараа бэлдэж байна",
  "Замд гарлаа",
  "Хүргэж байна",
  "Тун удахгүй хүрнэ",
  "Хүргэлт хойшлогдлоо",
]

interface Props {
  dn: IDeliveryNotePlus | null
  opened: boolean
  onClose: () => void
  onSuccess?: () => void
}

export function ProgressNoteModal({ dn, opened, onClose, onSuccess }: Props) {
  const { t } = useTranslation()
  const { permissionState } = useDeliveryActions()
  const perm = permissionState("notifyBuyer")
  const [preset, setPreset] = useState<string>(PRESETS[0])
  const [comment, setComment] = useState("")
  const [sending, setSending] = useState(false)

  const { data: auditData, mutate: mutateAudit } = useSWR(
    opened && dn?.id ? `/ord/delivery_management/${dn.id}/audit-log.progress` : null,
    () => DeliveryApi.auditLog(dn!.id as string),
    { revalidateOnFocus: false },
  )

  const history: IDeliveryAuditLogEntry[] = (auditData?.rows ?? []).filter(
    (r) => r.actionType === "DELIVERY_PROGRESS_NOTE",
  )

  useEffect(() => {
    if (opened) {
      setPreset(PRESETS[0])
      setComment("")
      setSending(false)
    }
  }, [opened])

  const finalNote = [preset, comment.trim()].filter(Boolean).join(" — ")

  const close = () => {
    if (sending) return
    onClose()
  }

  const onSend = async () => {
    if (!finalNote || !dn?.id) return
    setSending(true)
    try {
      await DeliveryApi.postProgressNote(dn.id, finalNote)
      message.success(
        t("delivery.progressNote.success", { defaultValue: "Мэдэгдэл худалдан авагчид илгээгдлээ" }),
      )
      setSending(false)
      mutateAudit()
      onSuccess?.()
      onClose()
    } catch (e) {
      message.error(translateBackendError(e))
      setSending(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      radius="lg"
      size={440}
      title={
        <Box>
          <Text fw={650} fz="var(--nw-fz-drawer)">
            {t("delivery:manage.notify.title")}
          </Text>
          <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)" ff="monospace">
            {[dn?.refCode, dn?.salesCode].filter(Boolean).join(" · ")}
          </Text>
        </Box>
      }
    >
      <Stack gap="md">
        {history.length > 0 && (
          <Box>
            <Group gap={6} mb={8}>
              <ClockCounterClockwiseIcon size={16} color="var(--nw-text-2)" />
              <Text fz="var(--nw-fz-body)" fw={600}>
                {t("delivery:manage.notify.history")}
              </Text>
              <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                ({history.length})
              </Text>
            </Group>
            <ScrollArea.Autosize mah={150}>
              <Stack gap={6} pr={6}>
                {history.map((h) => (
                  <Box key={h.id} className={classes.note}>
                    <Text fz="var(--nw-fz-body)" className={classes.preWrap}>
                      {h.notes}
                    </Text>
                    <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)" mt={2}>
                      {dateTimeSecFormat(h.occurredAt)}
                    </Text>
                  </Box>
                ))}
              </Stack>
            </ScrollArea.Autosize>
            <Divider mt="md" />
          </Box>
        )}

        <Box>
          <Text fz="var(--nw-fz-body)" fw={600} mb={8}>
            {t("delivery:manage.notify.status")}
          </Text>
          <Chip.Group value={preset} onChange={(v) => setPreset(v as string)}>
            <Group gap="xs">
              {PRESETS.map((p) => (
                <Chip key={p} value={p} radius="xl" color="dark" variant="outline">
                  {p}
                </Chip>
              ))}
            </Group>
          </Chip.Group>
        </Box>

        <Box>
          <Text fz="var(--nw-fz-body)" fw={600} mb={8}>
            {t("delivery:manage.notify.comment")}
          </Text>
          <Textarea
            autosize
            minRows={3}
            maxRows={7}
            maxLength={MAX_LENGTH}
            value={comment}
            onChange={(e) => setComment(e.currentTarget.value)}
            placeholder={t("delivery:manage.notify.placeholder")}
            disabled={sending}
          />
        </Box>

        {finalNote && (
          <Box className={classes.note}>
            <Text fz="var(--nw-fz-body)" className={classes.preWrap}>
              {t("delivery:manage.notify.preview")}{" "}
              <Text span fw={600}>
                {finalNote}
              </Text>
            </Text>
          </Box>
        )}

        <Stack gap={4} align="flex-end">
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={close} disabled={sending}>
              {t("delivery:manage.common.cancel")}
            </Button>
            <DeliveryActionButton state={perm} color="dark" onClick={onSend} loading={sending} disabled={!finalNote}>
              {t("delivery:manage.notify.send")}
            </DeliveryActionButton>
          </Group>
          {!perm.allowed && (
            <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
              {perm.reason}
            </Text>
          )}
        </Stack>
      </Stack>
    </Modal>
  )
}
